import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

const AlertModal = ({ 
  isOpen, 
  onClose, 
  title = 'Alert',
  message,
  buttonText = 'OK',
  variant = 'info', // 'danger', 'warning', 'info', 'success'
  icon
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => modalRef.current?.focus(), 50);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const variantConfig = {
    danger: {
      iconBg: 'rgba(255, 77, 77, 0.1)',
      iconBorder: 'rgba(255, 77, 77, 0.2)',
      iconColor: '#FF4D4D',
      buttonBg: 'linear-gradient(135deg, #DC2626 0%, #FF4D4D 100%)',
      buttonHover: 'linear-gradient(135deg, #FF4D4D 0%, #FF6B6B 100%)',
      buttonShadow: '0 4px 15px rgba(255, 77, 77, 0.3)',
      buttonHoverShadow: '0 6px 25px rgba(255, 77, 77, 0.4)',
      buttonText: '#FFFFFF'
    },
    warning: {
      iconBg: 'rgba(255, 176, 32, 0.1)',
      iconBorder: 'rgba(255, 176, 32, 0.2)',
      iconColor: '#FFB020',
      buttonBg: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
      buttonHover: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
      buttonShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
      buttonHoverShadow: '0 6px 25px rgba(245, 158, 11, 0.4)',
      buttonText: '#000'
    },
    info: {
      iconBg: 'rgba(0, 188, 212, 0.1)',
      iconBorder: 'rgba(0, 188, 212, 0.2)',
      iconColor: '#00BCD4',
      buttonBg: 'linear-gradient(135deg, #00ACC1 0%, #00BCD4 100%)',
      buttonHover: 'linear-gradient(135deg, #00BCD4 0%, #26C6DA 100%)',
      buttonShadow: '0 4px 15px rgba(0, 188, 212, 0.3)',
      buttonHoverShadow: '0 6px 25px rgba(0, 188, 212, 0.4)',
      buttonText: '#000'
    },
    success: {
      iconBg: 'rgba(50, 205, 50, 0.1)',
      iconBorder: 'rgba(50, 205, 50, 0.2)',
      iconColor: '#32CD32',
      buttonBg: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
      buttonHover: 'linear-gradient(135deg, #32CD32 0%, #66BB6A 100%)',
      buttonShadow: '0 4px 15px rgba(50, 205, 50, 0.3)',
      buttonHoverShadow: '0 6px 25px rgba(50, 205, 50, 0.4)',
      buttonText: '#000'
    }
  };

  const config = variantConfig[variant];

  const variantIcons = {
    danger: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M15 9l-6 6M9 9l6 6"></path>
      </svg>
    ),
    warning: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
    ),
    info: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    ),
    success: (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    )
  };

  const displayIcon = icon || variantIcons[variant];

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-bg-primary/70 backdrop-blur-lg animate-fadeIn"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div 
        ref={modalRef}
        tabIndex={-1}
        className="w-full max-w-sm rounded-xl overflow-hidden bg-bg-elevated/95 border border-primary-500/15 shadow-[0_25px_50px_rgba(3,6,4,0.5),0_0_40px_rgba(50,205,50,0.1)] animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-primary-500/10 bg-gradient-to-b from-primary-500/5 to-transparent">
          <h2 className="text-base font-semibold text-text-primary">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-all text-text-tertiary hover:text-primary-500 hover:bg-primary-500/10"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <div className="flex items-start gap-3">
            <div 
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
              style={{
                background: config.iconBg,
                border: `1.5px solid ${config.iconBorder}`,
                color: config.iconColor
              }}
            >
              {displayIcon}
            </div>
            <div className="flex-1 pt-0.5">
              <p className="text-sm leading-relaxed text-text-primary">
                {message}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3 border-t border-primary-500/10 bg-bg-primary/30">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold rounded-lg transition-all hover:-translate-y-0.5"
            style={{
              background: config.buttonBg,
              color: config.buttonText,
              boxShadow: config.buttonShadow
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 200ms ease-out;
        }
        .animate-slideUp {
          animation: slideUp 250ms ease-out;
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

AlertModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  variant: PropTypes.oneOf(['danger', 'warning', 'info', 'success']),
  icon: PropTypes.node
};

export default AlertModal;
